import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormLabel,
  Input,
  Modal as ChakraModal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import ReactSelect from 'react-select'

import { ITeacherFields, TeacherService } from '../../../services/Teacher/TeacherService'
import { TeacherComplete, KnowledgeArea, Course, TeachingFormat } from '../../../services/Teacher/types'

interface IProps {
  isOpen: boolean
  onClose: () => void
  teacher?: TeacherComplete
}

const baseForm: ITeacherFields = {
  name: '',
  banner_picture: null,
  profile_picture: null,
  profile_picture_thumbnail: null,
  iam_picture: null,
  highlight_phrase: '',
  iam_text: '',
  staff_body_text: '',
  teaching_formats: [],
  knowledge_areas: [],
  courses: [],
  enabled: true
}

const baseFormErrors = {
  name: '',
  banner_picture: '',
  profile_picture: '',
  profile_picture_thumbnail: '',
  iam_picture: '',
  highlight_phrase: '',
  iam_text: '',
  staff_body_text: '',
  teaching_formats: '',
  knowledge_areas: '',
  courses: '',
  enabled: ''
}

const teachingFormatOptions = [
  { value: 'IN_PERSON', label: 'Presencial' },
  { value: 'LIVE_ONLINE', label: 'Online ao Vivo' }
]

export function TeacherFormModal({ isOpen, onClose, teacher }: IProps) {
  const service = new TeacherService()
  const [saving, setSaving] = useState(false)
  const toast = useToast()
  const [form, setForm] = useState<ITeacherFields>(baseForm)
  const [knowledgeAreas, setKnowledgeAreas] = useState<KnowledgeArea[]>([])
  const [courses, setCourses] = useState<Course[]>([])
  const [formErrors, setFormErrors] = useState(baseFormErrors)

  useEffect(() => {
    if (isOpen) {
      setSaving(false)
      setFormErrors(baseFormErrors)

      // Load knowledge areas and courses
      service.getKnowledgeAreas().then(setKnowledgeAreas)
      service.getCourses().then(setCourses)

      setForm(
        teacher
          ? {
              name: teacher.name,
              banner_picture: null,
              profile_picture: null,
              profile_picture_thumbnail: null,
              iam_picture: null,
              highlight_phrase: teacher.highlight_phrase,
              iam_text: teacher.iam_text,
              staff_body_text: teacher.staff_body_text,
              teaching_formats: teacher.teaching_formats,
              knowledge_areas: teacher.knowledge_areas.map((area) => area.id),
              courses: teacher.courses.map((course) => course.id),
              enabled: teacher.enabled
            }
          : baseForm
      )
    }
  }, [isOpen, teacher])

  const handleSave = async () => {
    setSaving(true)

    const saveFunction = teacher ? service.update(teacher.id, form) : service.create(form)

    saveFunction
      .then(() => {
        toast({
          title: 'Professor salvo com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true
        })
        onClose()
      })
      .catch((response) => {
        if (response.response?.status === 422) {
          setFormErrors(response.response.data.errors)
          return
        }

        toast({
          title: teacher ? 'Erro ao editar professor' : 'Erro ao adicionar professor',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      })
      .finally(() => {
        setSaving(false)
      })
  }

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} size="4xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{teacher ? 'Editar' : 'Cadastrar'} Professor</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={4}>
            <Flex direction="row" gap={3}>
              <Box w="70%">
                <FormLabel htmlFor="name">Nome *</FormLabel>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  id="name"
                  disabled={saving}
                />
                {formErrors.name && (
                  <Text color="red.500" fontSize="sm">
                    {formErrors.name}
                  </Text>
                )}
              </Box>

              <Box w="30%" display="flex" alignItems="center" justifyContent="center">
                <Checkbox
                  isChecked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                  disabled={saving}
                >
                  Ativo
                </Checkbox>
              </Box>
            </Flex>

            <Box>
              <FormLabel htmlFor="teaching_formats">Formatos de Ensino *</FormLabel>
              <ReactSelect
                id="teaching_formats"
                value={form.teaching_formats.map((format) => ({
                  label: teachingFormatOptions.find((opt) => opt.value === format)?.label || format,
                  value: format
                }))}
                disabled={saving}
                isMulti
                closeMenuOnSelect={false}
                options={teachingFormatOptions}
                onChange={(selectedOptions: Array<{ value: TeachingFormat; label: string }>) => {
                  const selectedValues = selectedOptions.map((option) => option.value)
                  setForm({ ...form, teaching_formats: selectedValues })
                }}
                placeholder="Selecione os formatos de ensino"
              />
              {formErrors.teaching_formats && (
                <Text color="red.500" fontSize="sm">
                  {formErrors.teaching_formats}
                </Text>
              )}
            </Box>

            <Box>
              <FormLabel htmlFor="knowledge_areas">Áreas de Conhecimento *</FormLabel>
              <ReactSelect
                id="knowledge_areas"
                value={form.knowledge_areas
                  .map((areaId) => {
                    const area = knowledgeAreas.find((ka) => ka.id === areaId)
                    return area ? { label: area.name, value: area.id } : null
                  })
                  .filter(Boolean)}
                disabled={saving}
                isMulti
                closeMenuOnSelect={false}
                options={knowledgeAreas.map((area) => ({
                  label: area.name,
                  value: area.id
                }))}
                onChange={(selectedOptions: Array<{ value: number; label: string }>) => {
                  const selectedValues = selectedOptions.map((option) => option.value)
                  setForm({ ...form, knowledge_areas: selectedValues })
                }}
                placeholder="Selecione as áreas de conhecimento"
              />
              {formErrors.knowledge_areas && (
                <Text color="red.500" fontSize="sm">
                  {formErrors.knowledge_areas}
                </Text>
              )}
            </Box>

            <Box>
              <FormLabel htmlFor="courses">Cursos *</FormLabel>
              <ReactSelect
                id="courses"
                value={form.courses
                  .map((courseId) => {
                    const course = courses.find((c) => c.id === courseId)
                    return course ? { label: course.name, value: course.id } : null
                  })
                  .filter(Boolean)}
                disabled={saving}
                isMulti
                closeMenuOnSelect={false}
                options={courses?.map((course) => ({
                  label: course.name,
                  value: course.id
                }))}
                onChange={(selectedOptions: Array<{ value: number; label: string }>) => {
                  const selectedValues = selectedOptions.map((option) => option.value)
                  setForm({ ...form, courses: selectedValues })
                }}
                placeholder="Selecione os cursos"
              />
              {formErrors.courses && (
                <Text color="red.500" fontSize="sm">
                  {formErrors.courses}
                </Text>
              )}
            </Box>

            <Flex direction="row" gap={3}>
              <Box>
                <FormLabel htmlFor="banner_picture">Imagem do Banner *</FormLabel>
                <Input
                  type="file"
                  id="banner_picture"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  p={1}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      setForm({ ...form, banner_picture: file })
                    }
                  }}
                  disabled={saving}
                  required={!teacher}
                />
                {formErrors.banner_picture && (
                  <Text color="red.500" fontSize="sm">
                    {formErrors.banner_picture}
                  </Text>
                )}
              </Box>

              <Box>
                <FormLabel htmlFor="profile_picture">Foto do Perfil *</FormLabel>
                <Input
                  type="file"
                  id="profile_picture"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  p={1}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      setForm({ ...form, profile_picture: file })
                    }
                  }}
                  disabled={saving}
                  required={!teacher}
                />
                {formErrors.profile_picture && (
                  <Text color="red.500" fontSize="sm">
                    {formErrors.profile_picture}
                  </Text>
                )}
              </Box>
              <Box>
                <FormLabel htmlFor="iam_picture">Imagem eu sou *</FormLabel>
                <Input
                  type="file"
                  id="iam_picture"
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  p={1}
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      setForm({ ...form, iam_picture: file })
                    }
                  }}
                  disabled={saving}
                  required={!teacher}
                />
                {formErrors.iam_picture && (
                  <Text color="red.500" fontSize="sm">
                    {formErrors.iam_picture}
                  </Text>
                )}
              </Box>
            </Flex>

            <Flex direction="row" gap={3}>
              <Box w="50%">
                <FormLabel htmlFor="highlight_phrase">Frase de Destaque *</FormLabel>
                <Input
                  value={form.highlight_phrase}
                  onChange={(e) => setForm({ ...form, highlight_phrase: e.target.value })}
                  required
                  id="highlight_phrase"
                  disabled={saving}
                  maxLength={255}
                />
                {formErrors.highlight_phrase && (
                  <Text color="red.500" fontSize="sm">
                    {formErrors.highlight_phrase}
                  </Text>
                )}
              </Box>

              <Box w="50%">
                <FormLabel htmlFor="iam_text">Texto eu sou *</FormLabel>
                <Input
                  value={form.iam_text}
                  onChange={(e) => setForm({ ...form, iam_text: e.target.value })}
                  required
                  id="iam_text"
                  disabled={saving}
                  maxLength={255}
                />
                {formErrors.iam_text && (
                  <Text color="red.500" fontSize="sm">
                    {formErrors.iam_text}
                  </Text>
                )}
              </Box>
            </Flex>

            <Box>
              <FormLabel htmlFor="staff_body_text">Texto Corpo Docente *</FormLabel>
              <Textarea
                value={form.staff_body_text}
                disabled={saving}
                height="110px"
                onChange={(e) => setForm({ ...form, staff_body_text: e.target.value })}
                required
              />
              {formErrors.staff_body_text && (
                <Text color="red.500" fontSize="sm">
                  {formErrors.staff_body_text}
                </Text>
              )}
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="magenta" mr={3} onClick={handleSave} type="submit" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
          <Button variant="ghost" onClick={onClose} type="button">
            Cancelar
          </Button>
        </ModalFooter>
      </ModalContent>
    </ChakraModal>
  )
}
